import { Injectable, Injector } from '@angular/core';
import { firstValueFrom, map, Observable, shareReplay, switchMap } from 'rxjs';
import { FirebaseWrapper } from '../firebase-wrapper/firebase-wrapper';

interface ItemsDictionary<T> {
  [itemId: string]: T;
}

interface CollectionRefItem {
  collectionPath: string;
}

/**
 * The abstract class that needs to be inherited in order to get access to the collection.
 * In the inheritor, implement buildPath() function in order to asynchronously build the path to the collection.
 */
@Injectable()
export abstract class AbstractRealtimeDbRepository<T extends { id: string }> {
  private firebaseWrapper: FirebaseWrapper;

  private collectionDbRefItem$: Observable<CollectionRefItem>;
  private allItemsDictionary$: Observable<ItemsDictionary<T>>;

  constructor(injector: Injector) {
    this.firebaseWrapper = injector.get(FirebaseWrapper);
    this.collectionDbRefItem$ = this.initCollectionRef();
  }

  /**
   * Observes items in the collection and reacts to changes in that collection.
   * @returns items dictionary where key is item id and value is item
   */
  observeItemsDictionary(): Observable<ItemsDictionary<T>> {
    if (!this.allItemsDictionary$) {
      this.allItemsDictionary$ = this.collectionDbRefItem$.pipe(
        switchMap((collectionDbRef) => this.firebaseWrapper.onValue(collectionDbRef.collectionPath)),
        map((dataSnapshot) => dataSnapshot.val() || {})
      );
    }

    return this.allItemsDictionary$;
  }

  /**
   * Observes items in the collection and reacts to changes in that collection.
   * @returns items array
   */
  observeAll(): Observable<T[]> {
    return this.observeItemsDictionary().pipe(
      map((itemsDictionary) => Object.values(itemsDictionary)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Observes specific item by provided id and reacts to changes of that item
   * @param id item id
   * @returns 
   */
  observeById(id: string): Observable<T | undefined> {
    return this.collectionDbRefItem$.pipe(
      switchMap((collectionDbRef) => this.firebaseWrapper.onValue(`${collectionDbRef.collectionPath}/${id}`)),
      map((dataSnapshot) => dataSnapshot.val()),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Adds or updates item in the collection.
   * Throws error if provided item does not have id.
   * @param item item to add/update
   */
  async setAsync(item: T): Promise<void> {
    this.checkSingleItemForId(item);
    const itemDbRef = await this.getItemPath(item.id);
    await this.firebaseWrapper.setAsync(itemDbRef, item);
  }

  /**
   * Adds or updates batch of items in the collection.
   * Throws error if any provided item in the array does not have id.
   * @param item item to add/update
   */
  async setBatchAsync(items: T[]): Promise<void> {
    this.checkBatchItemsForId(items);
    const dbRefItem = await firstValueFrom(this.collectionDbRefItem$);
    const itemsDictionary: ItemsDictionary<T> = items.reduce(
      (result, currentValue) => ({ ...result, [currentValue.id]: currentValue }),
      {}
    );
    await this.firebaseWrapper.setAsync(dbRefItem.collectionPath, itemsDictionary);
  }

  /**
   * Removes item from the collection
   * @param id item id
   */
  async removeAsync(id: string): Promise<void> {
    const itemPath = await this.getItemPath(id);

    await this.firebaseWrapper.removeAsync(itemPath);
  }

  /**
   * Used to asynchronously build path to the collection
   */
  protected abstract buildPathAsync(): Promise<string>;

  private async getItemPath(itemId: string): Promise<string> {
    const dbRefItem = await firstValueFrom(this.collectionDbRefItem$);
    return `${dbRefItem.collectionPath}/${itemId}`;
  }

  private initCollectionRef(): Observable<CollectionRefItem> {
    return new Observable<CollectionRefItem>((subscriber) => {
      this.buildPathAsync().then((path) => {
        subscriber.next({ collectionPath: path });
      });
    }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  private checkSingleItemForId(item: T): void {
    if (!item.id) {
      throw new Error('The item should have id.');
    }
  }

  private checkBatchItemsForId(items: T[]): void {
    items.forEach((item, index) => {
      if (!item.id) {
        throw new Error(`The item with index '${index}' should have id.`);
      }
    });
  }
}
